import { useState, useEffect } from 'react';
import type { QuizAnswer, LeakType, Archetype } from '../page';

interface QuizSectionProps {
  currentQuestion: number;
  onAnswer: (answer: QuizAnswer) => void;
}

interface Question {
  id: number;
  question: string;
  highlightWords?: string[];
  answers: {
    text: string;
    leakType: LeakType;
    severity: number;
    archetypeMarkers: Archetype[];
  }[];
}

const questions: Question[] = [
  {
    id: 1,
    question: 'When you sit down to work, what happens?',
    highlightWords: ['happens'],
    answers: [
      { 
        text: "I'm not sure what to prioritise", 
        leakType: 'Decision Drift', 
        severity: 3,
        archetypeMarkers: ['Decision Drift']
      },
      { 
        text: 'I jump between tasks', 
        leakType: 'Focus Fragmentation', 
        severity: 3,
        archetypeMarkers: ['Fragmented Focus']
      },
      { 
        text: 'I tweak small things', 
        leakType: 'Busy Work Loop', 
        severity: 2,
        archetypeMarkers: ['Tool Overload']
      },
      { 
        text: 'I respond to messages first', 
        leakType: 'Focus Fragmentation', 
        severity: 2,
        archetypeMarkers: ['Reactive Operator']
      }
    ]
  },
  {
    id: 2,
    question: 'Your tool stack feels…',
    highlightWords: ['feels'],
    answers: [
      { 
        text: 'Overcrowded', 
        leakType: 'Tool Chaos', 
        severity: 4,
        archetypeMarkers: ['Tool Overload']
      },
      { 
        text: 'Messy but manageable', 
        leakType: 'Tool Chaos', 
        severity: 2,
        archetypeMarkers: ['Fragmented Focus']
      },
      { 
        text: 'Expensive', 
        leakType: 'Tool Chaos', 
        severity: 3,
        archetypeMarkers: ['Tool Overload']
      },
      { 
        text: 'Necessary but chaotic', 
        leakType: 'Tool Chaos', 
        severity: 3,
        archetypeMarkers: ['Tool Overload']
      }
    ]
  },
  {
    id: 3,
    question: 'At the end of the week you feel…',
    highlightWords: ['feel'],
    answers: [
      { 
        text: 'Busy but unclear', 
        leakType: 'Decision Drift', 
        severity: 3,
        archetypeMarkers: ['Decision Drift']
      },
      { 
        text: 'Productive but scattered', 
        leakType: 'Focus Fragmentation', 
        severity: 2,
        archetypeMarkers: ['Fragmented Focus']
      },
      { 
        text: 'Behind', 
        leakType: 'Busy Work Loop', 
        severity: 4,
        archetypeMarkers: ['Reactive Operator']
      },
      { 
        text: 'Mentally drained', 
        leakType: 'Focus Fragmentation', 
        severity: 3,
        archetypeMarkers: ['Decision Drift']
      }
    ]
  },
  {
    id: 4,
    question: 'Decisions in your business are…',
    highlightWords: ['Decisions'],
    answers: [
      { 
        text: 'Delayed', 
        leakType: 'Decision Drift', 
        severity: 4,
        archetypeMarkers: ['Reactive Operator', 'Decision Drift']
      },
      { 
        text: 'Rushed', 
        leakType: 'Decision Drift', 
        severity: 3,
        archetypeMarkers: ['Reactive Operator']
      },
      { 
        text: 'Reactive', 
        leakType: 'Decision Drift', 
        severity: 3,
        archetypeMarkers: ['Reactive Operator']
      },
      { 
        text: 'Revisited often', 
        leakType: 'Decision Drift', 
        severity: 2,
        archetypeMarkers: ['Fragmented Focus']
      }
    ]
  },
  {
    id: 5,
    question: 'Your to-do list…',
    highlightWords: ['to-do'],
    answers: [
      { 
        text: 'Grows daily', 
        leakType: 'Busy Work Loop', 
        severity: 3,
        archetypeMarkers: ['Tool Overload']
      },
      { 
        text: 'Gets rewritten', 
        leakType: 'Decision Drift', 
        severity: 2,
        archetypeMarkers: ['Reactive Operator']
      },
      { 
        text: 'Feels overwhelming', 
        leakType: 'Busy Work Loop', 
        severity: 4,
        archetypeMarkers: ['Decision Drift']
      },
      { 
        text: 'Has too many "shoulds"', 
        leakType: 'Busy Work Loop', 
        severity: 3,
        archetypeMarkers: ['Fragmented Focus']
      }
    ]
  },
  {
    id: 6,
    question: 'Be honest. Your biggest leak is probably…',
    highlightWords: ['biggest leak'],
    answers: [
      { 
        text: 'Clarity', 
        leakType: 'Decision Drift', 
        severity: 3,
        archetypeMarkers: ['Decision Drift']
      },
      { 
        text: 'Focus', 
        leakType: 'Focus Fragmentation', 
        severity: 3,
        archetypeMarkers: ['Fragmented Focus']
      },
      { 
        text: 'Tools', 
        leakType: 'Tool Chaos', 
        severity: 3,
        archetypeMarkers: ['Tool Overload']
      },
      { 
        text: 'Time', 
        leakType: 'Busy Work Loop', 
        severity: 3,
        archetypeMarkers: ['Reactive Operator']
      }
    ]
  }
];

const progressMessages = [
  'Calibrating…',
  'Analysing pattern…',
  'Mapping your workflow…',
  'Detecting clarity signals…'
];

export default function QuizSection({ currentQuestion, onAnswer }: QuizSectionProps) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const question = questions[currentQuestion - 1];

  useEffect(() => {
    setIsVisible(false);
    setSelectedIndex(null);
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, [currentQuestion]);

  const handleAnswerClick = (answer: { text: string; leakType: LeakType; severity: number; archetypeMarkers: Archetype[] }, index: number) => {
    if (isAnimating) return;
    
    setIsAnimating(true);
    setSelectedIndex(index);
    
    setTimeout(() => {
      onAnswer({
        questionId: question.id,
        answer: answer.text,
        leakType: answer.leakType,
        severity: answer.severity,
        archetypeMarkers: answer.archetypeMarkers
      });
      setIsAnimating(false);
    }, 400);
  };

  const progress = (currentQuestion / 6) * 100;
  const progressMessage = progressMessages[(currentQuestion - 1) % progressMessages.length];

  const highlightText = (text: string, words?: string[]) => {
    if (!words || words.length === 0) return text;
    
    let result = text;
    words.forEach(word => {
      const regex = new RegExp(`(${word})`, 'gi');
      result = result.replace(regex, '<span class="text-[#e87a02] relative inline-block after:content-[\'\'] after:absolute after:bottom-0 after:left-0 after:w-full after:h-0.5 after:bg-[#e87a02] after:animate-[underline_0.3s_ease-in-out]">$1</span>');
    });
    
    return result;
  };

  return (
    <section className="flex items-center justify-center min-h-screen px-6 py-12">
      <div className="max-w-2xl w-full">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-500">Question {currentQuestion} of 6</span>
            <span className="text-sm text-gray-500">{Math.round(progress)}%</span>
          </div>
          <div className="w-full h-1 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#e87a02] transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 mt-2 transition-opacity duration-300">
            {progressMessage}
          </p>
        </div>

        <div className={`transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <h2 
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 leading-tight"
            dangerouslySetInnerHTML={{ __html: highlightText(question.question, question.highlightWords) }}
          />

          <div className="space-y-4">
            {question.answers.map((answer, index) => (
              <button
                key={index}
                onClick={() => handleAnswerClick(answer, index)}
                disabled={isAnimating}
                className={`w-full text-left px-8 py-5 border-2 rounded-lg text-lg text-gray-800 transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50 ${
                  selectedIndex === index
                    ? 'border-[#e87a02] bg-[#e87a02]/5 scale-[1.02] shadow-[0_0_20px_rgba(232,122,2,0.15)]'
                    : 'border-gray-200 hover:border-gray-400 hover:bg-gray-50 hover:scale-[1.01]'
                }`}
              >
                {answer.text}
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}